import MadapiService from '@/services/madapiService'

// Evina DCBprotect anti-fraud kit (MTN Cameroon). The kit must be in the page BEFORE the subscriber
// taps the protected element (the Confirm button, #subscribe_button) so it can observe that click;
// the integration service then asks Evina for the verdict (/check) under the same transaction id (ti)
// before it calls MTN, and confirms the activation (/transaction) when MTN's callback lands. Mirrors
// client.telkom-za-web-marketing-ssr and client.mtn-uganda-marketing-wr.
//
// VUE_APP_EVINA_MODE: service (kit issued by service.madapi-integration POST /evina/script, the
// production shape) | script (a hosted snippet, VUE_APP_EVINA_SCRIPT_URL) | off (local dev).
export const EVINA_TARGET = '#subscribe_button'

const GATEWAY_LOAD_TIMEOUT_MS = 8000
const mode = String(process.env.VUE_APP_EVINA_MODE || 'service').toLowerCase()
const scriptUrl = process.env.VUE_APP_EVINA_SCRIPT_URL || ''

let injected = false
let armedKey = ''
let armedTi = null

export function evinaEnabled() {
  return mode !== 'off'
}

function decode(base64) {
  try {
    return atob(base64)
  } catch {
    return ''
  }
}

function waitForGateway() {
  return new Promise((resolve) => {
    document.addEventListener('gateway-load', () => resolve(true), { once: true })
    setTimeout(() => resolve(false), GATEWAY_LOAD_TIMEOUT_MS)
  })
}

// Evina returns either raw JS or <script> tags (inline or src); scripts set via innerHTML never
// execute, so each one is recreated.
function appendScripts(markup) {
  if (!/^\s*<script/i.test(markup)) {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.text = markup
    document.body.appendChild(script)
    return
  }
  const parsed = new DOMParser().parseFromString(markup, 'text/html')
  parsed.querySelectorAll('script').forEach((node) => {
    const script = document.createElement('script')
    for (const attribute of node.attributes) script.setAttribute(attribute.name, attribute.value)
    if (!node.getAttribute('src')) script.text = node.textContent
    document.body.appendChild(script)
  })
}

function appendScriptUrl(url) {
  const script = document.createElement('script')
  script.src = url
  script.async = true
  document.body.appendChild(script)
}

// Arms the kit for this (number, product): one Evina transaction per attempt. The same pair just
// re-binds the button; a changed number or tier fetches a fresh kit and a fresh ti.
export async function armEvina({ msisdn, serviceId, productId, transactionId }) {
  if (!evinaEnabled()) return { ok: true, skipped: true, ti: null }
  if (!productId) return { ok: false, reason: 'missing-product', ti: null }

  const key = `${productId}:${msisdn || ''}`
  if (injected && armedKey === key) {
    rearmEvina()
    return { ok: true, reused: true, ti: armedTi }
  }

  const ready = waitForGateway()
  let ti = null
  try {
    if (mode === 'script') {
      if (!scriptUrl) return { ok: false, reason: 'no-script-url', ti: null }
      appendScriptUrl(scriptUrl)
    } else {
      const kit = await MadapiService.getEvinaScript({ targetElement: EVINA_TARGET, msisdn, serviceId, productId, transactionId })
      if (kit?.result !== 'success' || !kit?.script) return { ok: false, reason: kit?.reason || kit?.result || 'no-script', ti: null }
      const markup = decode(kit.script)
      if (!markup) return { ok: false, reason: 'bad-script', ti: null }
      appendScripts(markup)
      ti = kit.ti || null
    }
  } catch (error) {
    return { ok: false, reason: error?.message || 'load-failed', ti: null }
  }

  injected = true
  armedKey = key
  armedTi = ti
  document.dispatchEvent(new Event('DCBProtectRun'))
  return { ok: true, gatewayLoaded: await ready, ti }
}

// The protected button is re-created when the step changes; ask the kit to bind again.
export function rearmEvina() {
  if (injected) document.dispatchEvent(new Event('DCBProtectRun'))
}
